import { useCallback, useEffect, useMemo, useState } from "react";
import Loader from "src/components/common/loader/loader";
import { EngagementService } from "@services/engagements/engagementService";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import { Objective } from "@models/objective.model";
import type { KeyDate, KeyDateType } from "@models/keyDate.model";
import type { Launch } from "@models/launch.model";
import KeyDateModal from "./KeyDateModal";
import ModalDelete from "src/components/common/ModalDelete";

type ListTypeOption = { value: string | number; label: string };

type Props = {
  engagementId: number;
  objectiveId: number;
  objectiveName: string;
  campaigns: Launch[];
  onBack: () => void;
  onOpenCampaign: (campaignId: number) => void;
  onRenamed: (objectiveId: number, name: string) => void;
};

/*
    The objective's own screen. Everything here describes the list the campaigns
    under it will run against — how big it is, how old, what kind, how far back
    to look — plus the dates the engagement is planned around.

    The form is edited as a draft and saved in one go: these fields are read
    together and set together, so save-per-keystroke would be both chatty and
    hard to reason about halfway through.
*/
export default function ObjectiveDetail({
  engagementId,
  objectiveId,
  objectiveName,
  campaigns,
  onBack,
  onOpenCampaign,
  onRenamed,
}: Props) {
  const [loaded, setLoaded] = useState<Objective | null>(null);
  const [draft, setDraft] = useState<Objective | null>(null);
  const [listTypes, setListTypes] = useState<ListTypeOption[]>([]);
  const [keyDates, setKeyDates] = useState<KeyDate[]>([]);
  const [keyDateTypes, setKeyDateTypes] = useState<KeyDateType[]>([]);
  const [editingKeyDate, setEditingKeyDate] = useState<KeyDate | "new" | null>(null);
  const [deletingKeyDate, setDeletingKeyDate] = useState<KeyDate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const service = useMemo(() => new EngagementService(), []);

  const loadKeyDates = useCallback(async () => {
    const dates = await service.getKeyDates(engagementId, objectiveId);
    setKeyDates(
      [...dates].sort(
        (a, b) => new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime(),
      ),
    );
  }, [service, engagementId, objectiveId]);

  /*
      Independent loads: a missing list-type catalogue should not hide the
      objective, and absent key dates should not hide the form.
  */
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    Promise.allSettled([
      service.getObjectiveData(engagementId, objectiveId),
      service.getObjectiveListTypeChoices(),
      loadKeyDates(),
      service.getKeyDateTypes(),
    ])
      .then(([objective, types, _dates, dateTypes]) => {
        if (cancelled) return;
        if (objective.status === "fulfilled") {
          setLoaded(objective.value);
          setDraft({ ...objective.value } as Objective);
        } else {
          setError("Could not load this objective.");
        }
        if (types.status === "fulfilled") setListTypes(types.value ?? []);
        if (dateTypes.status === "fulfilled") setKeyDateTypes(dateTypes.value ?? []);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [service, engagementId, objectiveId, loadKeyDates]);

  const set = <K extends keyof Objective>(key: K, value: Objective[K]) => {
    setSaved(false);
    setDraft((prev) => (prev ? ({ ...prev, [key]: value } as Objective) : prev));
  };

  // Number inputs clear to null rather than 0 — "not recorded" and "zero" are
  // different answers, and the backend keeps them apart.
  const setNumber = (key: "list_size" | "lookback_window", raw: string) => {
    const trimmed = raw.trim();
    set(key, (trimmed === "" ? null : Number(trimmed)) as never);
  };

  const isDirty = useMemo(() => {
    if (!loaded || !draft) return false;
    const watched: (keyof Objective)[] = [
      "name",
      "list_size",
      "list_age_start_date",
      "list_age_end_date",
      "list_type",
      "lookback_window",
    ];
    return watched.some((key) => (draft[key] ?? null) !== (loaded[key] ?? null));
  }, [loaded, draft]);

  const datesOutOfOrder =
    !!draft?.list_age_start_date &&
    !!draft?.list_age_end_date &&
    draft.list_age_start_date > draft.list_age_end_date;

  const handleSave = async () => {
    if (!draft) return;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await service.updateObjective(engagementId, objectiveId, draft);
      setLoaded(updated);
      setDraft({ ...updated } as Objective);
      setSaved(true);
      if (updated.name && updated.name !== objectiveName) onRenamed(objectiveId, updated.name);
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch {
      setError("Could not save the objective. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteKeyDate = async () => {
    if (deletingKeyDate?.id == null) return;
    try {
      await service.deleteKeyDate(engagementId, objectiveId, deletingKeyDate.id);
      setDeletingKeyDate(null);
      await loadKeyDates();
      workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
    } catch {
      setError("Could not delete that key date. Try again.");
      setDeletingKeyDate(null);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="campaign-pane">
      <div className="campaign-head">
        <button type="button" className="campaign-head__back" onClick={onBack}>
          ‹ Campaigns
        </button>
        <h5>{loaded?.name?.trim() || objectiveName}</h5>
        <span className="campaign-head__objective">Objective</span>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {draft && (
        <>
          <div className="campaign-label">The list</div>
          <div className="campaign-card obj-form">
            <div className="obj-field obj-field--wide">
              <label htmlFor="objName">Objective name</label>
              <input
                id="objName"
                className="form-control"
                value={draft.name ?? ""}
                onChange={(e) => set("name", e.target.value)}
              />
            </div>

            <div className="obj-field">
              <label htmlFor="listSize">List size</label>
              <input
                id="listSize"
                className="form-control"
                type="number"
                min={0}
                inputMode="numeric"
                placeholder="Not recorded"
                value={draft.list_size ?? ""}
                onChange={(e) => setNumber("list_size", e.target.value)}
              />
              <span className="obj-field__hint">How many records the client sent.</span>
            </div>

            <div className="obj-field">
              <label htmlFor="listType">List type</label>
              <select
                id="listType"
                className="form-select"
                value={draft.list_type ?? ""}
                onChange={(e) => set("list_type", e.target.value)}
              >
                <option value="">Not set</option>
                {listTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              <span className="obj-field__hint">
                A static list is sent once; a dynamic one keeps arriving.
              </span>
            </div>

            <div className="obj-field">
              <label htmlFor="lookback">Lookback window</label>
              <div className="obj-field__suffixed">
                <input
                  id="lookback"
                  className="form-control"
                  type="number"
                  min={0}
                  inputMode="numeric"
                  placeholder="Not set"
                  value={draft.lookback_window ?? ""}
                  onChange={(e) => setNumber("lookback_window", e.target.value)}
                />
                <span className="obj-field__suffix">days</span>
              </div>
              <span className="obj-field__hint">How far back behaviour is counted.</span>
            </div>

            {/* List age is a span, not two unrelated dates, so it reads as one control. */}
            <div className="obj-field obj-field--wide">
              <label htmlFor="ageStart">List age</label>
              <div className="obj-range">
                <input
                  id="ageStart"
                  className="form-control"
                  type="date"
                  value={draft.list_age_start_date ?? ""}
                  onChange={(e) => set("list_age_start_date", e.target.value || null)}
                />
                <span className="obj-range__to">to</span>
                <input
                  id="ageEnd"
                  className="form-control"
                  type="date"
                  value={draft.list_age_end_date ?? ""}
                  onChange={(e) => set("list_age_end_date", e.target.value || null)}
                />
              </div>
              <span className={`obj-field__hint${datesOutOfOrder ? " obj-field__hint--warn" : ""}`}>
                {datesOutOfOrder
                  ? "The start of the range is after its end."
                  : "The window the records were gathered over."}
              </span>
            </div>

            <div className="obj-form__actions">
              {saved && !isDirty && <span className="obj-form__saved">Saved</span>}
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleSave}
                disabled={!isDirty || isSaving || datesOutOfOrder}
              >
                {isSaving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="campaign-label mt-4">Key dates</div>
      <div className="campaign-card">
        <div className="obj-sec__head">
          <span className="obj-sec__title">{keyDates.length || "No"} dates</span>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm ms-auto"
            style={{ fontSize: "0.72rem" }}
            onClick={() => setEditingKeyDate("new")}
          >
            + Key date
          </button>
        </div>
        {keyDates.length === 0 ? (
          <div className="campaign-section__empty">
            Nothing dated yet. Key dates are the milestones the campaigns are planned around.
          </div>
        ) : (
          keyDates.map((keyDate) => (
            <div className="obj-date" key={keyDate.id}>
              <span className="obj-date__when">{formatKeyDate(keyDate.date)}</span>
              <span className="obj-date__what">
                {keyDate.label?.trim() || keyDate.date_type || "Untitled date"}
              </span>
              {keyDate.is_custom && <span className="campaign-pill campaign-pill--muted">Custom</span>}
              <button
                type="button"
                className="obj-date__action ms-auto"
                onClick={() => setEditingKeyDate(keyDate)}
              >
                Edit
              </button>
              <button
                type="button"
                className="obj-date__action obj-date__action--danger"
                onClick={() => setDeletingKeyDate(keyDate)}
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>

      {campaigns.length > 0 && (
        <>
          <div className="campaign-label mt-4">Campaigns in this objective</div>
          <div className="campaign-card">
            {campaigns.map((campaign) => (
              <button
                key={campaign.id}
                type="button"
                className="campaign-row"
                onClick={() => onOpenCampaign(campaign.id)}
              >
                <span className="campaign-row__main">
                  <span className="campaign-row__name">{campaign.name}</span>
                </span>
                <span className="campaign-row__chev">›</span>
              </button>
            ))}
          </div>
        </>
      )}

      {editingKeyDate && (
        <KeyDateModal
          engagementId={engagementId}
          objectiveId={objectiveId}
          keyDate={editingKeyDate === "new" ? null : editingKeyDate}
          keyDateTypes={keyDateTypes}
          onClose={() => setEditingKeyDate(null)}
          onSaved={async () => {
            setEditingKeyDate(null);
            await loadKeyDates();
            workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
          }}
        />
      )}

      {deletingKeyDate && (
        <ModalDelete
          visible
          title="key date"
          objectLabel={
            deletingKeyDate.label?.trim() || deletingKeyDate.date_type || formatKeyDate(deletingKeyDate.date)
          }
          isLoading={false}
          toggleVisibility={() => setDeletingKeyDate(null)}
          deleteFn={handleDeleteKeyDate}
        />
      )}
    </div>
  );
}

function formatKeyDate(value: string | null | undefined): string {
  if (!value) return "No date";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "No date";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
